/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {animate, state, style, transition, trigger} from '@angular/animations';
import {HttpErrorResponse} from '@angular/common/http';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input} from '@angular/core';

import {TranslateService} from '@ngx-translate/core';

import {timer} from 'rxjs';
import {takeUntil, tap} from 'rxjs/operators';

import {ClipboardService} from 'ngx-clipboard';
import {Destroyable} from '../../destroyable.component';
import {MESSAGE_TYPE} from '../ui';

/**
 * Alert modal, similar in spirit as the browser 'window.alert' method. Presents the user with an alert dialog
 * with an button to close it.
 *
 * Typically used directly from the modal service using :
 *
 * ```
 * this.modalService.alert({
 *   title: 'My alert title',
 *   text: 'My alert text'
 * });
 * ```
 */
@Component({
  selector: 'gp-modal-alert',
  template: `
    <div class="modal modal-alert in" style="display: block;">
      <div class="modal-dialog" [ngClass]="{'modal-lg': size == 'lg'}">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ title | translate }}
            </h5>
          </div>
          <div class="modal-body p-lg">
            <div *ngIf="!fancyDialogOptions?.errorType; else fancyDialogBlock">
              <div>{{ text | translate}}</div>
              <div *ngIf="detail" class="detail" (click)="toggle()">
                <span>{{'ERROR.details' | translate}}</span>
                <span
                  class="fa fa-light text-muted"
                  [ngClass]="{
                             'fa-sort-down vertical-align-open': areDetailsShowed,
                             'fa-sort-up vertical-align-close': !areDetailsShowed
                }"
                >
              </span>
              </div>
              <div *ngIf="customInfo" class="highlight" [@showDetail]="detailState">
                <!--                <div *ngIf="detail?.code"><b>{{'ERROR.status_code' | translate}}:</b> {{detail?.code}}</div>-->
                <div *ngIf="customInfo?.message || customInfo?.description">
                  <b>{{'ERROR.description' | translate}}:</b>
                  <span [innerHTML]="(customInfo?.message || customInfo?.description) | translate"></span>
                </div>
              </div>
            </div>
            <ng-template #fancyDialogBlock>
              <gp-message *ngIf="isCopied"
                          class="text-center"
                          text="ERROR.copied_to_clipboard_success_msg" type="success"></gp-message>
              <gp-illustrated-message *ngIf="fancyDialogOptions?.errorType != 'BLOCKING_ERROR' else blockingError"
                                      [text]="fancyDialogOptions?.description | translate"
                                      [hintText]="'ERROR.copy_details_instruction_msg'"
                                      [type]="fancyDialogOptions?.errorType"
                                      [width]="'80%'">
              </gp-illustrated-message>
              <ng-template #blockingError>
                <gp-illustrated-message [text]="fancyDialogOptions?.description | translate"
                                        [subText]="'FANCY.BLOCKING_ERRORS.text'"
                                        [hintText]="'FORM_GENERATOR.BLOCKING_ERRORS.correct'"
                                        [type]="fancyDialogOptions?.errorType"
                                        [width]="'80%'">
                </gp-illustrated-message>
              </ng-template>
            </ng-template>
          </div>
          <div class="modal-footer">
            <a *ngIf="fancyDialogOptions?.errorType && fancyDialogOptions?.errorType != 'BLOCKING_ERROR'"
               test-id="copyErrorMssLink"
               class="text-primary p-r-md pointer"
               (click)="copyToClipboard()">
              <i class="fa fa-light fa-clipboard" aria-hidden="true"></i>
              {{'ERROR.copy_details' | translate}}
            </a>
            <button type="button" class="btn btn-ok-white p-x-md" (click)="done()">
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./alert-modal.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('showDetail', [
      state('close', style({
        visibility: 'hidden',
        height: '0',
        padding: '0 1rem',
        'margin-bottom': '0rem'
      })),
      state('open', style({
        height: '*',
        padding: '*',
        visibility: 'visible',
        'margin-bottom': '1rem'
      })),
      transition('close => open', animate('150ms ease-in')),
      transition('open => close', animate('150ms ease-out'))
    ])
  ]
})
export class AlertModalComponent extends Destroyable {
  /**
   * @type {string} One of 'lg', ...
   */
  @Input()
  public size: string = '';

  /**
   * @type {string} Title of the alert modal
   */
  @Input()
  public title: string = 'Alert';

  /**
   * @type {string} Body text of the alert modal
   */
  @Input()
  public text: string = '';

  @Input()
  public detail: HttpErrorResponse | Error;

  @Input()
  public fancyDialogOptions: {
    errorType?: MESSAGE_TYPE,
    description?: string
  } = null;

  @Input()
  public customInfo: {
    description?: string,
    message?: string
  } = null;


  /**
   * @type {EventEmitter<boolean>} Result of the confirm dialog depending on user interaction
   */
  public result: EventEmitter<void> = new EventEmitter<void>();

  public detailState: string = 'open';
  public areDetailsShowed: boolean = true;
  public isCopied = false;

  constructor(
    private translateService: TranslateService,
    private clipboardService: ClipboardService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    super();
  }

  public done() {
    this.result.emit(undefined);
    this.result.complete();
  }

  public toggle() {
    this.areDetailsShowed = !this.areDetailsShowed;
    this.detailState = !this.areDetailsShowed ? 'close' : 'open';
  }

  public copyToClipboard(): void {
    const raw = this.customInfo?.message || this.customInfo?.description || '';
    const text = raw ? this.translateService.instant(raw) : '';
    this.clipboardService.copy(text);

    this.isCopied = true;
    this.changeDetectorRef.markForCheck();

    timer(3000).pipe(
      tap(() => {
        this.isCopied = false;
        this.changeDetectorRef.markForCheck();
      }),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }
}
